import classNames from "classnames";
import { alpha2ToAlpha3, alpha3ToAlpha2 } from "i18n-iso-countries";
import { CountryCode, getExampleNumber, isValidNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { FC, memo, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";

import "flag-icons/css/flag-icons.min.css";

import { PhoneNumberCodeType } from "entities/common";
import useClickOutside from "hooks/useClickOutside";

import TextInput from "components/TextInput";

import PhoneNumberCode from "./PhoneNumberCode";

type PropTypes = {
  autoComplete?: string;
  error?: string | boolean;
  onChange: (phone: string, isValidNumber?: boolean) => void;
  phoneNumberCodeClassName?: string;
  value?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  className?: string;
};

const initialCode = {
  alpha3Code: "ARE",
  callingCode: "+971",
};

const PhoneNumber: FC<PropTypes> = ({
  error,
  value,
  onChange,
  autoComplete,
  phoneNumberCodeClassName,
  isRequired,
  isDisabled,
  className,
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [code, setCode] = useState<Pick<PhoneNumberCodeType, "alpha3Code" | "callingCode">>(initialCode);
  const [phone, setPhone] = useState("");
  const [selectedInput, setSelectedInput] = useState(false);

  const currentMask = useMemo(() => getExampleMask(String(alpha3ToAlpha2(String(code.alpha3Code)))), [code.alpha3Code]);

  const handlePhoneChange = (event: SyntheticEvent) => {
    const currentValue = (event.target as HTMLInputElement).value.replace(/\D/g, "");
    const cursorPosition = ref.current?.selectionStart || 0;
    const correctedCursorPosition = setCorrectCursorPosition(currentMask, cursorPosition);

    setPhone(changeMaskOnValue(currentMask, currentValue));

    queueMicrotask(() => {
      if (ref.current) {
        ref.current.setSelectionRange(correctedCursorPosition, correctedCursorPosition);
      }
    });
  };

  const handleBackspace = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = ref.current;

    if (!input) return;

    const { selectionStart, selectionEnd } = input;

    // if selected all input (ctrl + a)
    if (event.key === "Backspace" && selectionStart === 0 && selectionEnd === phone.length) {
      event.preventDefault();
      setPhone(currentMask);

      queueMicrotask(() => {
        if (ref.current) {
          ref.current.setSelectionRange(0, 0);
        }
      });

      return;
    }

    // if removed by one symbol
    if (event.key === "Backspace") {
      const currentValue = phone.replace(/\D/g, "");
      const newValue = currentValue.slice(0, -1);
      const cursorPosition = selectionStart || 0;
      let correctedCursorPosition = setCorrectCursorPosition(currentMask, cursorPosition);

      setPhone(changeMaskOnValue(currentMask, newValue));

      if (cursorPosition > 0 && currentMask[cursorPosition - 1] !== "_") {
        correctedCursorPosition -= 1;
      }

      queueMicrotask(() => {
        if (ref.current) {
          ref.current.setSelectionRange(correctedCursorPosition, correctedCursorPosition);
        }
      });
    }
  };

  const handleInputClick = () => {
    queueMicrotask(() => {
      const input = ref.current;
      let cursorPosition = 0;

      if (!input) return;

      setSelectedInput(true);

      const hasRealDigits = /\d/.test(phone);

      if (hasRealDigits) {
        for (let i = phone.length; i >= 0; i--) {
          if (/[0-9]/.test(phone[i])) {
            cursorPosition = i + 1;
            break;
          }
        }
      }

      input.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleCodeChange = (data: PhoneNumberCodeType) => {
    setCode({
      alpha3Code: data.alpha3Code,
      callingCode: data.callingCode,
    });
    setPhone(currentMask);
  };

  // set phone mask
  useEffect(() => {
    if (currentMask) {
      setPhone(currentMask);
    }
  }, [currentMask]);

  // onChange if isValid
  useEffect(() => {
    if (code.callingCode) {
      const number = `${code.callingCode}${phone.replace(/-/g, "").replace(/_/g, "")}`;

      onChange(number, isValidNumber(number));
    }
  }, [code.callingCode, phone]);

  // parse outside value to component value
  useEffect(() => {
    const parsedCode = parsePhoneNumberFromString(value || "");

    if (parsedCode && isValidNumber(value || "")) {
      setPhone(changeMaskOnValue(currentMask, parsedCode?.nationalNumber || ""));
      setCode({
        alpha3Code: alpha2ToAlpha3(parsedCode?.country || "") || "",
        callingCode: `+${parsedCode?.countryCallingCode}`,
      });
    } else if (!value) {
      setPhone(currentMask);
      setCode(initialCode);
    }
  }, [value]);

  useClickOutside(ref, () => setSelectedInput(false));

  return (
    <div ref={rootRef} className={classNames("phone-number", className)}>
      <PhoneNumberCode
        isRequired={isRequired}
        value={code}
        onChange={handleCodeChange}
        isDisabled={isDisabled}
        className={phoneNumberCodeClassName}
        alignRef={rootRef}
        error={error ? !!error : undefined}
      />

      <TextInput
        ref={ref}
        value={phone}
        isRequired={isRequired}
        onClick={selectedInput ? () => {} : handleInputClick}
        onKeyDown={handleBackspace}
        onChange={handlePhoneChange}
        autoComplete={autoComplete}
        isDisabled={isDisabled}
        label="Phone number"
        className={classNames("phone-number__text-input", {
          "phone-number__text-input_empty": !phone.replace(/[-_]/g, "").length,
        })}
        error={error ? String(error) : undefined}
      />
    </div>
  );
};

function formatAsMask(exampleNumber: string): string {
  return exampleNumber.replace(/\d/g, "_").replace(/\s/g, "-");
}

export function changeMaskOnValue(currentMask: string, value: string) {
  let count = 0;

  return currentMask
    .split("")
    .map((char, index) => {
      const currentSymbol = value[index - count];

      if (char === "_" && currentSymbol) {
        return currentSymbol;
      }

      if (char === "-") {
        count += 1;
      }

      return char;
    })
    .join("");
}

export function getExampleMask(countryCode: string): string {
  const exampleNumber = getExampleNumber(countryCode as CountryCode, examples);

  if (exampleNumber) {
    return formatAsMask(exampleNumber?.formatInternational()?.split(" ")?.slice(1)?.join(" "));
  }

  return "";
}

function setCorrectCursorPosition(currentMask: string, selectionStart: number) {
  let newCursorPosition = 0;

  for (let i = 0; i < selectionStart; i++) {
    if (currentMask[i] === "_") {
      newCursorPosition += 1;
    }
  }

  let correctedCursorPosition = 0;

  for (let i = 0; i < newCursorPosition; i++) {
    correctedCursorPosition += 1;
    while (correctedCursorPosition < currentMask.length && currentMask[correctedCursorPosition] !== "_") {
      correctedCursorPosition += 1;
    }
  }

  return correctedCursorPosition;
}

export default memo(PhoneNumber);
